import React, { useState } from 'react';
import { Link, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { Mail, Lock, Loader2 } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const LoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { login } = useAuth();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  
  // Get redirect URL from query params or location state
  const redirectUrl = searchParams.get('redirect') || location.state?.from || '/dashboard';

  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await fetch(`${BACKEND_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      let data;
      try {
        data = await response.json();
      } catch (parseError) {
        console.error('Failed to parse response:', parseError);
        throw new Error('Server error. Please try again.');
      }

      if (!response.ok) {
        throw new Error(data.detail || 'Login failed');
      }

      if (!data.access_token) {
        throw new Error('Invalid server response. Please try again.');
      }

      login(data.access_token, data.user);
      toast.success('Welcome back!');
      
      // Redirect to the intended page or dashboard
      navigate(redirectUrl);
    } catch (error) {
      console.error('Login error:', error);
      // Handle specific error types
      if (error.name === 'TypeError') {
        toast.error('Network error. Please check your connection and try again.');
      } else {
        toast.error(error.message || 'Login failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Header />
      
      <main className="flex-1 pt-20 pb-12 px-4 flex items-center">
        <div className="max-w-md mx-auto w-full">
          <Card className="shadow-lg">
            <CardHeader className="text-center">
              {/* Laikos Logo */}
              <div className="flex justify-center mb-4">
                <img 
                  src="https://customer-assets.emergentagent.com/job_laikos-school/artifacts/tkonto33_IMG_1836.jpeg"
                  alt="Laikos Lay Schools Logo"
                  className="w-24 h-24 object-contain rounded-full"
                  style={{ background: 'transparent' }}
                />
              </div>
              <CardTitle className="text-2xl font-bold text-gray-800">Welcome Back</CardTitle>
              <CardDescription>Login to your Laikos Lay Schools account</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email" className="flex items-center gap-2">
                    <Mail size={16} /> Email
                  </Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="Enter your email"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password" className="flex items-center gap-2">
                    <Lock size={16} /> Password
                  </Label>
                  <Input
                    id="password"
                    name="password"
                    type="password"
                    value={formData.password}
                    onChange={handleChange}
                    placeholder="Enter your password"
                    required
                  />
                </div>

                <div className="text-right">
                  <Link 
                    to={redirectUrl !== '/dashboard' ? `/forgot-password?redirect=${encodeURIComponent(redirectUrl)}` : '/forgot-password'} 
                    className="text-sm text-[#5f7c8a] hover:underline"
                  >
                    Forgot Password?
                  </Link>
                </div>

                <Button 
                  type="submit" 
                  className="w-full bg-[#5f7c8a] hover:bg-[#4a6470]"
                  disabled={loading}
                >
                  {loading ? (
                    <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Logging in...</>
                  ) : (
                    'Login'
                  )}
                </Button>
              </form>

              <p className="text-center mt-6 text-sm text-gray-600">
                Don't have an account?{' '}
                <Link to="/register" className="text-[#5f7c8a] hover:underline font-medium">
                  Register here
                </Link>
              </p>
            </CardContent>
          </Card>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default LoginPage;
